import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { toast } from 'sonner';
import axios from 'axios';
import { resolveAssetUrl } from '../utils/resolveAssetUrl';
import { treeHref, personHref } from '../utils/prettyArchiveLinks';
import { useAuth } from '../contexts/AuthContext';
import {
  Search,
  ArrowLeft,
  Dna,
  MapPin,
  Calendar,
  User,
  ChevronDown,
  ChevronUp,
  Plus,
  FileText,
  Clock,
  Sparkles,
  Filter,
  X,
  Database,
  TreePine,
  Loader2,
  Shield,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  Users
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confidence tier styling
const confidenceTiers = {
  verified: {
    label: 'Verified',
    color: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/20',
    icon: CheckCircle2,
    description: 'High confidence match based on multiple data points'
  },
  likely: {
    label: 'Likely',
    color: 'bg-amber-500/10 text-amber-700 border-amber-500/20',
    icon: AlertCircle,
    description: 'Probable match with supporting evidence'
  },
  possible: {
    label: 'Possible',
    color: 'bg-slate-500/10 text-slate-600 border-slate-500/20',
    icon: HelpCircle,
    description: 'Potential connection requiring further research'
  }
};

// Match result card component
const MatchCard = ({ match, index, onAddToTree }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const tier = confidenceTiers[match.confidence_tier] || confidenceTiers.possible;
  const TierIcon = tier.icon;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Card className="group relative overflow-hidden border-border/40 bg-card/50 backdrop-blur-sm hover:shadow-lg hover:shadow-[#2C4A3B]/5 transition-all duration-300">
        {/* Match score indicator bar */}
        <div 
          className="absolute top-0 left-0 h-1 bg-gradient-to-r from-[#2C4A3B] to-[#C16644] transition-all duration-500"
          style={{ width: `${match.match_score}%` }}
        />
        
        <CardContent className="p-6">
          <div className="flex items-start gap-4">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {match.profile_image_url ? (
                <img
                  src={resolveAssetUrl(BACKEND_URL, match.profile_image_url)}
                  alt={match.full_name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-border/40"
                />
              ) : (
                <div className="w-16 h-16 rounded-full bg-[#2C4A3B]/10 flex items-center justify-center border-2 border-border/40">
                  <span className="text-lg font-serif text-[#2C4A3B]">
                    {match.given_names?.[0]}{match.surname?.[0]}
                  </span>
                </div>
              )}
              {/* Score badge */}
              <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-[#2C4A3B] text-white text-xs font-bold flex items-center justify-center shadow-lg">
                {Math.round(match.match_score)}%
              </div>
            </div>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-serif font-medium text-foreground truncate">
                    {match.full_name}
                  </h3>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    From: {match.tree_name}
                  </p>
                </div>
                
                {/* Confidence tier badge */}
                <Badge variant="outline" className={`${tier.color} flex items-center gap-1.5 flex-shrink-0`}>
                  <TierIcon className="w-3.5 h-3.5" />
                  {tier.label}
                </Badge>
              </div>

              {/* Key details */}
              <div className="flex flex-wrap gap-4 mt-3 text-sm text-muted-foreground">
                {match.birth_year && (
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-4 h-4" />
                    <span>{match.birth_year}{match.death_date ? ` - ${match.death_date.slice(0, 4)}` : ''}</span>
                  </div>
                )}
                {match.birth_place && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    <span className="truncate max-w-[200px]">{match.birth_place}</span>
                  </div>
                )}
              </div>

              {/* Relationship type */}
              {match.relationship_type && (
                <div className="mt-3">
                  <Badge variant="secondary" className="bg-[#C16644]/10 text-[#C16644] border-[#C16644]/20">
                    <TreePine className="w-3.5 h-3.5 mr-1.5" />
                    {match.relationship_type}
                  </Badge>
                </div>
              )}
            </div>
          </div>

          {/* Expandable details section */}
          <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
            <CollapsibleTrigger asChild>
              <button className="w-full mt-4 pt-4 border-t border-border/40 flex items-center justify-between text-sm text-muted-foreground hover:text-foreground transition-colors">
                <span className="flex items-center gap-2">
                  <Database className="w-4 h-4" />
                  Match Analysis & Source Records
                </span>
                {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </CollapsibleTrigger>
            
            <CollapsibleContent>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mt-4 space-y-4"
              >
                {/* Match breakdown */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-[#2C4A3B]">{match.name_similarity}%</div>
                    <div className="text-xs text-muted-foreground mt-1">Name Match</div>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-[#2C4A3B]">{match.date_proximity}%</div>
                    <div className="text-xs text-muted-foreground mt-1">Date Proximity</div>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-[#2C4A3B]">{match.location_overlap}%</div>
                    <div className="text-xs text-muted-foreground mt-1">Location Match</div>
                  </div>
                </div>

                {/* Timeline position */}
                {match.timeline_position && (
                  <div className="bg-muted/20 rounded-lg p-4 border border-border/40">
                    <div className="flex items-center gap-2 text-sm font-medium mb-2">
                      <Clock className="w-4 h-4 text-[#C16644]" />
                      Timeline Position
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Lived during the {match.timeline_position.era} era
                      {match.timeline_position.death_year && 
                        ` (${match.timeline_position.birth_year} - ${match.timeline_position.death_year})`
                      }
                    </p>
                  </div>
                )}

                {/* Source records placeholder */}
                <div className="bg-muted/20 rounded-lg p-4 border border-dashed border-border/60">
                  <div className="flex items-center gap-2 text-sm font-medium mb-2">
                    <FileText className="w-4 h-4 text-muted-foreground" />
                    Source Records
                  </div>
                  <p className="text-sm text-muted-foreground">
                    No source records attached yet. Census, archive, and military records can be linked in a future update.
                  </p>
                  {match.source_records.length > 0 && (
                    <div className="mt-2 space-y-1">
                      {match.source_records.map((record, i) => (
                        <div key={i} className="text-xs bg-background/50 rounded px-2 py-1">
                          {record.type}: {record.value} ({record.status})
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Bio preview */}
                {match.bio && (
                  <div className="text-sm text-muted-foreground">
                    <p className="line-clamp-3">{match.bio}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3 pt-2">
                  <Link to={personHref({ id: match.id, slug: match.slug })} className="flex-1">
                    <Button variant="outline" className="w-full rounded-full" size="sm">
                      <User className="w-4 h-4 mr-2" />
                      View Profile
                    </Button>
                  </Link>
                  <Link to={treeHref({ id: match.tree_id, slug: match.tree_slug })} className="flex-1">
                    <Button className="w-full rounded-full bg-[#2C4A3B] hover:bg-[#2C4A3B]/90" size="sm">
                      <Plus className="w-4 h-4 mr-2" />
                      View in Tree
                    </Button>
                  </Link>
                </div>
              </motion.div>
            </CollapsibleContent>
          </Collapsible>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const GeneEngine = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Get URL search params for pre-filling from insights
  const [searchParams] = React.useState(() => new URLSearchParams(window.location.search));
  
  // Search state - initialize from URL params if present
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('name') || '');
  const [showAdvanced, setShowAdvanced] = useState(() => {
    // Show advanced if any advanced params are in URL
    return !!(searchParams.get('yearStart') || searchParams.get('yearEnd') || searchParams.get('location'));
  });
  const [advancedFilters, setAdvancedFilters] = useState(() => ({
    birthYearStart: searchParams.get('yearStart') || '',
    birthYearEnd: searchParams.get('yearEnd') || '',
    location: searchParams.get('location') || '',
    recordReference: ''
  }));
  
  // Track if we came from insights (for auto-search)
  const [fromInsights] = useState(() => searchParams.has('name') || searchParams.has('location'));
  
  // Surname cluster search toggle (default OFF)
  const [includeClusters, setIncludeClusters] = useState(false);
  const [clusterResults, setClusterResults] = useState(null);
  const [clusterLoading, setClusterLoading] = useState(false);
  
  // Results state
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  
  // Fetch stats on mount
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/gene-engine/stats`);
        setStats(response.data);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      }
    };
    fetchStats();
  }, []);

  // Perform search
  const performSearch = useCallback(async () => {
    if (!searchQuery.trim() && !advancedFilters.birthYearStart && !advancedFilters.location) {
      toast.error('Please enter a search term or use advanced filters');
      return;
    }
    
    setLoading(true);
    setClusterResults(null);
    
    try {
      const response = await axios.post(`${API}/gene-engine/search`, {
        full_name: searchQuery.trim() || null,
        partial_name: searchQuery.trim() || null,
        birth_year_start: advancedFilters.birthYearStart ? parseInt(advancedFilters.birthYearStart) : null,
        birth_year_end: advancedFilters.birthYearEnd ? parseInt(advancedFilters.birthYearEnd) : null,
        location: advancedFilters.location.trim() || null,
        record_reference: advancedFilters.recordReference.trim() || null
      });
      
      setResults(response.data);
      
      // Also search surname clusters if enabled
      if (includeClusters && searchQuery.trim()) {
        setClusterLoading(true);
        try {
          const clusterParams = new URLSearchParams();
          clusterParams.set('surname', searchQuery.trim());
          if (advancedFilters.location) clusterParams.set('region', advancedFilters.location.trim());
          if (advancedFilters.birthYearStart) clusterParams.set('year_start', advancedFilters.birthYearStart);
          if (advancedFilters.birthYearEnd) clusterParams.set('year_end', advancedFilters.birthYearEnd);
          
          const clusterResponse = await axios.get(`${API}/surname-clusters/search?${clusterParams.toString()}`);
          setClusterResults(clusterResponse.data);
        } catch (clusterError) {
          console.error('Cluster search failed:', clusterError);
        } finally {
          setClusterLoading(false);
        }
      }
      
      if (response.data.total_matches === 0) {
        toast.info('No matches found. Try adjusting your search criteria.');
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, advancedFilters, includeClusters]);

  // Auto-search when arriving from insights with pre-filled params (one-shot on mount)
  useEffect(() => {
    if (fromInsights && (searchQuery || advancedFilters.location || advancedFilters.birthYearStart)) {
      const timer = setTimeout(() => {
        performSearch();
      }, 500);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot auto-search when navigating from insights; not on every keystroke
  }, [fromInsights]);

  // Handle enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      performSearch();
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
    setAdvancedFilters({
      birthYearStart: '',
      birthYearEnd: '',
      location: '',
      recordReference: ''
    });
    setResults(null);
    setClusterResults(null);
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="border-b border-border/40 bg-background/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/home')}
                className="rounded-full"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-xl font-serif font-semibold text-[#2C4A3B]" data-testid="page-title">
                  Gene Identity Engine™
                </h1>
                <p className="text-xs text-muted-foreground">Relative Discovery System</p>
              </div>
            </div>
            <Link to="/home" className="text-sm text-[#2C4A3B] hover:underline hidden sm:block">
              Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero section */}
      <section className="relative py-12 lg:py-16 overflow-hidden">
        {/* Subtle background pattern */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, #2C4A3B 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }} />
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Icon */}
            <div className="w-16 h-16 rounded-2xl bg-[#2C4A3B]/10 flex items-center justify-center mx-auto mb-6">
              <Dna className="w-8 h-8 text-[#2C4A3B]" />
            </div>
            
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-light text-[#1C1917] mb-4">
              Discover Your Bloodline
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              Search across your family trees to uncover potential relatives, trace ancestral connections, 
              and build a deeper understanding of your heritage.
            </p>

            {/* Stats strip */}
            {stats && (
              <div className="flex flex-wrap justify-center gap-6 sm:gap-10 mb-10 text-sm">
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#2C4A3B]">{stats.total_individuals}</div>
                  <div className="text-muted-foreground">Individuals</div>
                </div>
                <div className="hidden sm:block w-px h-12 bg-border/60" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#2C4A3B]">{stats.total_trees}</div>
                  <div className="text-muted-foreground">Family Trees</div>
                </div>
                <div className="hidden sm:block w-px h-12 bg-border/60" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#2C4A3B]">
                    {stats.earliest_year ? `${stats.earliest_year}` : '—'}
                  </div>
                  <div className="text-muted-foreground">Earliest Record</div>
                </div>
                <div className="hidden sm:block w-px h-12 bg-border/60" />
                <div className="text-center">
                  <div className="text-2xl font-bold text-[#2C4A3B]">{stats.unique_locations}</div>
                  <div className="text-muted-foreground">Locations</div>
                </div>
              </div>
            )}
          </motion.div>

          {/* Search bar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-2xl mx-auto"
          >
            {/* Investigation Context Banner - shows when coming from Family Insights */}
            {fromInsights && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-4 p-3 bg-[#2C4A3B]/5 border border-[#2C4A3B]/20 rounded-xl text-left"
                data-testid="investigation-context-banner"
              >
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-[#2C4A3B]/10 flex items-center justify-center flex-shrink-0">
                    <Search className="w-4 h-4 text-[#2C4A3B]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#2C4A3B] uppercase tracking-wide mb-1">
                      Investigation Context
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Search pre-filled from Family Insights. 
                      {searchQuery && <span className="font-medium"> Searching for "{searchQuery}"</span>}
                      {advancedFilters.location && <span className="font-medium"> in {advancedFilters.location}</span>}
                      {advancedFilters.birthYearStart && <span className="font-medium"> ({advancedFilters.birthYearStart}-{advancedFilters.birthYearEnd || '...'})</span>}
                    </p>
                  </div>
                  <button
                    onClick={clearSearch}
                    className="p-1 rounded-full hover:bg-[#2C4A3B]/10 transition-colors"
                    title="Clear and start fresh"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
              </motion.div>
            )}

            <div className="relative">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search by name (e.g., John Williams, Mary O'Brien)..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="h-14 pl-12 pr-12 text-base rounded-full border-border/60 bg-card shadow-lg shadow-[#2C4A3B]/5 focus:ring-2 focus:ring-[#2C4A3B]/20"
                    data-testid="search-input"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                <Button
                  onClick={performSearch}
                  disabled={loading}
                  className="h-14 px-6 rounded-full bg-[#2C4A3B] hover:bg-[#2C4A3B]/90 shadow-lg shadow-[#2C4A3B]/20"
                  data-testid="search-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Search className="w-5 h-5" />
                  )}
                </Button>
              </div>

              {/* Advanced filters toggle */}
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="mt-4 flex items-center gap-2 mx-auto text-sm text-muted-foreground hover:text-foreground transition-colors"
                data-testid="advanced-toggle"
              >
                <Filter className="w-4 h-4" />
                Advanced Filters
                {showAdvanced ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {/* Advanced filters panel */}
              <AnimatePresence>
                {showAdvanced && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-4 p-6 bg-card rounded-2xl border border-border/40 shadow-lg text-left">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Birth Year Range</Label>
                          <div className="flex gap-2 items-center">
                            <Input
                              type="number"
                              placeholder="From"
                              min="1"
                              max="3999"
                              value={advancedFilters.birthYearStart}
                              onChange={(e) => setAdvancedFilters(f => ({ ...f, birthYearStart: e.target.value }))}
                              className="h-10"
                              data-testid="birth-year-start"
                            />
                            <span className="text-muted-foreground">—</span>
                            <Input
                              type="number"
                              placeholder="To"
                              min="1"
                              max="3999"
                              value={advancedFilters.birthYearEnd}
                              onChange={(e) => setAdvancedFilters(f => ({ ...f, birthYearEnd: e.target.value }))}
                              className="h-10"
                              data-testid="birth-year-end"
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm font-medium">Location</Label>
                          <div className="relative">
                            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              type="text"
                              placeholder="City, state, or country"
                              value={advancedFilters.location}
                              onChange={(e) => setAdvancedFilters(f => ({ ...f, location: e.target.value }))}
                              className="h-10 pl-10"
                              data-testid="location-input"
                            />
                          </div>
                        </div>
                        <div className="space-y-2 sm:col-span-2">
                          <Label className="text-sm font-medium">Historical Record Reference</Label>
                          <div className="relative">
                            <FileText className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                              type="text"
                              placeholder="Census ID, archive reference, military record..."
                              value={advancedFilters.recordReference}
                              onChange={(e) => setAdvancedFilters(f => ({ ...f, recordReference: e.target.value }))}
                              className="h-10 pl-10"
                              data-testid="record-reference-input"
                            />
                          </div>
                        </div>
                      </div>

                      {/* DNA placeholder */}
                      <div className="mt-4 p-4 bg-muted/30 rounded-xl border border-dashed border-border/60">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#2C4A3B]/10 flex items-center justify-center">
                            <Dna className="w-5 h-5 text-[#2C4A3B]/50" />
                          </div>
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">DNA Marker Upload</p>
                            <p className="text-xs text-muted-foreground/70">Coming in a future update</p>
                          </div>
                          <Badge variant="outline" className="ml-auto text-xs">Future</Badge>
                        </div>
                      </div>
                      
                      {/* Discoverable Clusters Toggle */}
                      <div className="mt-4 p-4 bg-[#C9A961]/5 rounded-xl border border-[#C9A961]/20">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl bg-[#C9A961]/10 flex items-center justify-center shrink-0">
                            <Users className="w-5 h-5 text-[#C9A961]" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="text-sm font-medium text-foreground">Include Discoverable Clusters</p>
                              <Badge variant="outline" className="text-[10px] bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30">Beta</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                              Search anonymized surname clusters from other trees that have opted in to discovery.
                              No personal data is shared.
                            </p>
                            <label className="flex items-center gap-2 cursor-pointer" data-testid="cluster-toggle">
                              <input
                                type="checkbox"
                                checked={includeClusters}
                                onChange={(e) => setIncludeClusters(e.target.checked)}
                                className="w-4 h-4 rounded border-[#C9A961]/40 text-[#C9A961] focus:ring-[#C9A961]/30"
                              />
                              <span className="text-sm text-foreground">
                                {includeClusters ? 'Enabled' : 'Enable cluster search'}
                              </span>
                            </label>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" size="sm" onClick={clearSearch}>
                          Clear All
                        </Button>
                        <Button 
                          size="sm" 
                          onClick={performSearch}
                          disabled={loading}
                          className="bg-[#2C4A3B] hover:bg-[#2C4A3B]/90 rounded-full"
                        >
                          Apply Filters
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Results section */}
      <section className="py-8 lg:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatePresence mode="wait">
            {loading && (
              <motion.div
                key="loading"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-16"
              >
                <Loader2 className="w-12 h-12 animate-spin text-[#2C4A3B] mx-auto mb-4" />
                <p className="text-muted-foreground">Analyzing your family data...</p>
              </motion.div>
            )}

            {!loading && results && (
              <motion.div
                key="results"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                {/* Results header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-serif font-medium">
                      {results.total_matches} Potential {results.total_matches === 1 ? 'Match' : 'Matches'}
                    </h3>
                    <p className="text-sm text-muted-foreground">{results.query_summary}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={clearSearch}>
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>

                {/* Confidence tier legend */}
                <div className="flex flex-wrap gap-4 mb-6 p-4 bg-muted/20 rounded-xl">
                  <div className="text-xs text-muted-foreground">Confidence Tiers:</div>
                  {Object.entries(confidenceTiers).map(([key, tier]) => (
                    <div key={key} className="flex items-center gap-1.5 text-xs">
                      <Badge variant="outline" className={`${tier.color} py-0.5`}>
                        {tier.label}
                      </Badge>
                    </div>
                  ))}
                </div>

                {/* Results list */}
                {results.matches.length > 0 ? (
                  <div className="space-y-4" data-testid="results-list">
                    {results.matches.map((match, index) => (
                      <MatchCard key={match.id} match={match} index={index} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-muted/30 flex items-center justify-center mx-auto mb-4">
                      <Search className="w-8 h-8 text-muted-foreground" />
                    </div>
                    <h3 className="text-lg font-serif font-medium mb-2">No Matches Found</h3>
                    <p className="text-muted-foreground max-w-md mx-auto">
                      Try broadening your search criteria or adding more individuals to your family trees.
                    </p>
                  </div>
                )}
                
                {/* Cluster Results Section */}
                {clusterLoading && (
                  <div className="mt-8 pt-8 border-t border-border/50 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-[#C9A961] mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">Searching discoverable clusters...</p>
                  </div>
                )}
                
                {!clusterLoading && clusterResults && clusterResults.matches.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-border/50" data-testid="cluster-results">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 rounded-lg bg-[#C9A961]/10 flex items-center justify-center">
                        <Users className="w-4 h-4 text-[#C9A961]" />
                      </div>
                      <div>
                        <h4 className="text-base font-medium">Discoverable Clusters</h4>
                        <p className="text-xs text-muted-foreground">
                          {clusterResults.total_matches} cluster{clusterResults.total_matches !== 1 ? 's' : ''} found • No personal data shared
                        </p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      {clusterResults.matches.map((cluster, index) => (
                        <div 
                          key={index} 
                          className="p-4 rounded-xl bg-[#C9A961]/5 border border-[#C9A961]/20"
                          data-testid={`cluster-match-${index}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-sm font-medium text-foreground mb-1">
                                {cluster.description}
                              </p>
                              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {cluster.region}
                                </span>
                                {cluster.decade !== 'Unknown period' && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {cluster.decade}
                                  </span>
                                )}
                              </div>
                            </div>
                            <Badge variant="outline" className="shrink-0 text-[10px] bg-[#C9A961]/10 text-[#C9A961] border-[#C9A961]/30">
                              {cluster.tree_count} {cluster.tree_count === 1 ? 'tree' : 'trees'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    <p className="mt-4 text-[10px] text-muted-foreground/60 text-center">
                      {clusterResults.disclaimer}
                    </p>
                  </div>
                )}
                
                {!clusterLoading && includeClusters && clusterResults && clusterResults.matches.length === 0 && (
                  <div className="mt-8 pt-8 border-t border-border/50 text-center">
                    <p className="text-sm text-muted-foreground">
                      No discoverable clusters found for this surname.
                    </p>
                  </div>
                )}
              </motion.div>
            )}

            {!loading && !results && (
              <motion.div
                key="empty"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center py-16"
              >
                <div className="w-20 h-20 rounded-2xl bg-[#2C4A3B]/5 flex items-center justify-center mx-auto mb-6">
                  <Sparkles className="w-10 h-10 text-[#2C4A3B]/40" />
                </div>
                <h3 className="text-xl font-serif font-light mb-2">Ready to Discover</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Enter a name, date range, or location above to search for potential relatives across your family trees.
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Info section */}
      <section className="py-12 bg-muted/20 border-t border-border/40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center p-6">
              <Shield className="w-8 h-8 text-[#2C4A3B] mx-auto mb-3" />
              <h4 className="font-medium mb-2">Private & Secure</h4>
              <p className="text-sm text-muted-foreground">
                Searches only your own trees. Your family data stays private.
              </p>
            </div>
            <div className="text-center p-6">
              <Sparkles className="w-8 h-8 text-[#C16644] mx-auto mb-3" />
              <h4 className="font-medium mb-2">Intelligent Matching</h4>
              <p className="text-sm text-muted-foreground">
                Heuristic algorithms analyze names, dates, and locations for likelihood scores.
              </p>
            </div>
            <div className="text-center p-6">
              <Clock className="w-8 h-8 text-[#2C4A3B] mx-auto mb-3" />
              <h4 className="font-medium mb-2">Timeline Ready</h4>
              <p className="text-sm text-muted-foreground">
                Matches include timeline data for easy integration with your family history.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GeneEngine;
